// helpers
import { ApiHelper, resetRefreshCooldown } from 'shared/helpers/api';
import { getNewCartHeader } from 'shared/api/helpers';
import { captureError } from 'shared/helpers/logger';
import { normalizeResponse } from './transformer';
import { markAuthenticatedSession } from 'shared/api/grahakSession';

// constants
import { ROUTES, getGrahakUrl } from 'shared/api/urls';
import { API_ERROR_MESSAGE } from 'shared/constants/messages';
import { DOMAIN } from 'shared/domainConfig/constants';

const DEFAULT_GRAHAK_GUEST_SESSION_MAX_RETRIES = 2;
const NO_ADDRESS_FOUND_CODE = 1010;

const getJsonOrReject = async (response: Response) => {
  if (!response.ok) {
    // Always consume the response body, even on error.  If we skip this the
    // browser cancels the ReadableStream and DevTools shows a blank
    // Preview/Response tab — making 4xx/5xx errors invisible to developers.
    // We also prefer the server's own error message when available.
    let serverMessage: string | undefined;
    try {
      const errorBody = await response.json();
      serverMessage = errorBody?.message || errorBody?.error;
    } catch {
      // Body was not JSON (e.g. HTML error page) — fall through to default.
    }
    return Promise.reject({
      statusCode: response.status,
      message: serverMessage || API_ERROR_MESSAGE,
    });
  }

  return response.json();
};

const normalizeLoggedInUserResponse = (response: any) => {
  const { response: legacyResponse, status, data } = response || {};

  if (legacyResponse?.customerData) {
    return response;
  }

  const { formKey = '', form_Key = '', cartCount = 0, userDetails: rawUserDetails } = data || {};
  const userDetails = rawUserDetails || {};
  const {
    userId = '',
    email = '',
    mobile = '',
    firstName = '',
    lastName = '',
    fullName = '',
    hideEmail,
    isEmailVerified = false,
    dob = '',
    gender,
    imageLink = '',
  } = userDetails;

  const hasUserDetails = Object.keys(userDetails).length > 0;

  return {
    // Fail closed: only mark as success when the upstream explicitly says so.
    // An empty/missing status used to default to 'success', which masked
    // partial/broken responses (no userDetails) as a successful login.
    status: status || 'fail',
    response: {
      form_Key: formKey || form_Key || '',
      customerData: hasUserDetails
        ? {
            id: userId,
            email,
            mobile,
            firstname: firstName || fullName,
            lastname: lastName,
            hideEmail: hideEmail === true,
            isEmailVerified,
            fullName,
            dob,
            gender: gender !== undefined ? String(gender) : '',
            imageLink,
            allowMergeAccount: false,
            newUser: 0,
          }
        : {},
      cart_count: cartCount,
      base_location: 'IN',
    },
  };
};

const normalizeAddress = (address: any) => {
  const {
    firstname = '',
    firstName,
    lastname = '',
    lastName,
    fullName: existingFullName,
    addressLineOne: line1A,
    addressLine1,
    address1,
    line1,
    addressLineTwo: line2A,
    addressLine2,
    address2,
    line2,
    isDefault: isDefaultRaw,
    default: defaultRaw,
    addressId,
    id,
    mobileNumber,
    mobile,
    pincode,
    postcode,
    state,
    region,
    regionCode = '',
    regionId = '',
    country = '',
    countryId,
    countryCode,
    isEditable,
  } = address || {};

  const normalizedFirstName = firstname || firstName || '';
  const normalizedLastName = lastname || lastName || '';
  const computedFullName = `${normalizedFirstName} ${normalizedLastName}`.trim();
  const addressLineOne = line1A || addressLine1 || address1 || line1 || '';
  const addressLineTwo = line2A || addressLine2 || address2 || line2 || '';
  const resolvedDefault = isDefaultRaw ?? defaultRaw;
  const normalizedDefault =
    typeof resolvedDefault === 'boolean' ? (resolvedDefault ? 1 : 0) : Number(resolvedDefault) || 0;

  return {
    ...address,
    addressId: addressId || id || '',
    mobileNumber: mobileNumber || mobile || '',
    fullName: existingFullName || computedFullName,
    pincode: pincode || postcode || '',
    state: state || region || '',
    regionCode,
    regionId,
    addressLineOne,
    addressLineTwo,
    country,
    countryId: countryId || countryCode || '',
    isDefault: normalizedDefault,
    isEditable: isEditable ?? 1,
  };
};

const normalizeAddressesResponse = (response: any) => {
  const { status, code, data, response: responseData } = response || {};

  if (status !== 'success' && code !== NO_ADDRESS_FOUND_CODE) {
    return Promise.reject(response);
  }

  if (code === NO_ADDRESS_FOUND_CODE) {
    return [];
  }

  const addresses =
    (Array.isArray(data) && data) || responseData?.addresses || data?.addresses || [];
  return addresses.map(normalizeAddress);
};

export const getGuestSession = async (
  retryCount = 0,
  maxRetries: number = DEFAULT_GRAHAK_GUEST_SESSION_MAX_RETRIES
): Promise<any> => {
  const url = getGrahakUrl(ROUTES.GUEST_SESSION);

  try {
    const res = await ApiHelper(url, {
      method: 'GET',
    });

    return await getJsonOrReject(res);
  } catch (err: any) {
    if (retryCount < maxRetries) {
      // Reuse the resolved budget across retries so we don't re-read the
      // store on every recursive call.
      return getGuestSession(retryCount + 1, maxRetries);
    }

    captureError(err, 'Failed to get guest session', url);
    throw err;
  }
};

type SendOtpInput = string | { mobileNumber?: string; email?: string };

export const sendOtp = async (mobileOrEmail: SendOtpInput): Promise<any> => {
  const body = {
    mobileOrEmail:
      typeof mobileOrEmail === 'string'
        ? mobileOrEmail
        : mobileOrEmail.mobileNumber || mobileOrEmail.email || '',
  };

  const res = await ApiHelper(getGrahakUrl(ROUTES.AUTH_INIT), {
    method: 'POST',
    isBodyJSON: true,
    body,
  });

  const data = await getJsonOrReject(res);

  const normalized = normalizeResponse('sendOtp', data);

  // Treat "success without otpId" as a transport-level failure so every
  // caller (login/resend/etc.) gets a uniform rejection path instead of
  // having to defensively check `response.otpId` after a "success" status.
  if (normalized.status === 'success' && !normalized.response?.otpId) {
    const error = new Error(normalized.message || 'Missing otpId in sendOtp response');
    captureError(error, 'sendOtp-missing-authId', getGrahakUrl(ROUTES.AUTH_INIT));
    return Promise.reject(error);
  }

  return normalized;
};

export const resendOtpJwt = async (authId: string): Promise<any> => {
  const body = { authId, requestIntegrityInfo: null };

  const res = await ApiHelper(getGrahakUrl(ROUTES.OTP_RESEND), {
    method: 'POST',
    isBodyJSON: true,
    body,
  });

  const data = await getJsonOrReject(res);

  return normalizeResponse('resendOtp', data);
};

export const verifyLoginJwt = async (
  authId: string,
  otp: string,
  _authToken?: string,
  timeoutValue?: number
): Promise<any> => {
  // _authToken is intentionally retained for legacy signature compatibility with login(...).

  if (!authId || authId.trim() === '') {
    const error = new Error('authId is required for verifyLoginJwt');
    captureError(error, 'verifyLoginJwt-empty-authId', getGrahakUrl(ROUTES.LOGIN_VERIFY));
    throw error;
  }

  const body = {
    authId,
    otp,
  };
  const res = await ApiHelper(
    `${getGrahakUrl(ROUTES.LOGIN_VERIFY)}?loginMethod=MOBILE_OTP`,
    {
      method: 'POST',
      isBodyJSON: true,
      body,
      headers: getNewCartHeader(),
    },
    timeoutValue
  );

  const json = await getJsonOrReject(res);
  const normalizedResponse = normalizeResponse('verifyJwt', json);

  if (normalizedResponse?.status === 'success') {
    markAuthenticatedSession();
    resetRefreshCooldown();
  }

  return normalizedResponse;
};

export const refreshToken = async (): Promise<any> => {
  const url = getGrahakUrl(ROUTES.TOKEN_REFRESH);

  const res = await ApiHelper(url, {
    method: 'POST',
    isBodyJSON: true,
    body: {},
  });
  const json = await getJsonOrReject(res);

  return json;
};

export const sendActionOTPAccountDelete = async (): Promise<any> => {
  const body = {
    action: 'DELETE_ACCOUNT',
  };

  const res = await ApiHelper(getGrahakUrl(ROUTES.SEND_ACTION_OTP), {
    method: 'POST',
    isBodyJSON: true,
    body,
  });

  const data = await getJsonOrReject(res);

  return normalizeResponse('sendActionOtp', data);
};

export const sendActionOTP = async (email: string): Promise<any> => {
  const body = {
    action: 'UPDATE_EMAIL',
    actionMeta: {
      newEmail: email,
    },
  };

  const res = await ApiHelper(getGrahakUrl(ROUTES.SEND_ACTION_OTP), {
    method: 'POST',
    isBodyJSON: true,
    body,
  });

  const data = await getJsonOrReject(res);

  return normalizeResponse('sendActionOtp', data);
};

export const resendActionOTP = async (authId: string): Promise<any> => {
  return resendOtpJwt(authId);
};

export const executeUserAction = async (authId: string, otp: string): Promise<any> => {
  if (!authId || authId.trim() === '') {
    const error = new Error('authId is required for executeUserAction');
    captureError(error, 'executeUserAction-empty-authId', getGrahakUrl(ROUTES.EXECUTE_USER_ACTION));
    throw error;
  }

  const body = {
    authId,
    otp,
    action: 'UPDATE_EMAIL',
  };

  const res = await ApiHelper(getGrahakUrl(ROUTES.EXECUTE_USER_ACTION), {
    method: 'POST',
    isBodyJSON: true,
    body,
  });

  const data = await getJsonOrReject(res);

  return normalizeResponse('executeAction', data);
};

export const executeUserActionAccountDelete = async (authId: string, otp: string): Promise<any> => {
  if (!authId || authId.trim() === '') {
    const error = new Error('authId is required for executeUserAction');
    captureError(error, 'executeUserAction-empty-authId', getGrahakUrl(ROUTES.EXECUTE_USER_ACTION));
    throw error;
  }

  const body = {
    authId,
    otp,
    action: 'DELETE_ACCOUNT',
  };

  const res = await ApiHelper(getGrahakUrl(ROUTES.EXECUTE_USER_ACTION), {
    method: 'POST',
    isBodyJSON: true,
    body,
  });

  const data = await getJsonOrReject(res);

  return normalizeResponse('executeAction', data);
};

export const getLoggedInUserJwt = (): Promise<any> => {
  const url = getGrahakUrl(ROUTES.UPDATE_USER);
  return ApiHelper(url, {
    method: 'GET',
    headers: getNewCartHeader(),
  })
    .then((data: Response) => getJsonOrReject(data))
    .then(normalizeLoggedInUserResponse);
};

export const getAddressesJwt = (): Promise<any> => {
  const url = getGrahakUrl(ROUTES.GRAHAK_GET_ADDRESSES);
  return ApiHelper(url, {
    method: 'GET',
    headers: {
      'x-web': '1',
    },
  })
    .then((data: Response) => getJsonOrReject(data))
    .then(normalizeAddressesResponse);
};

export const checkAccountDeleteEligibilityJwt = (): Promise<any> => {
  const url = getGrahakUrl(ROUTES.GRAHAK_CHECK_ACCOUNT_DELETE_ELIGIBILITY);
  return ApiHelper(`${url}?domain=${DOMAIN.NYKAA_FASHION}`, {
    method: 'GET',
  }).then((data: Response) => {
    if (!data.ok) {
      return Promise.reject({ statusCode: data?.status, message: API_ERROR_MESSAGE });
    }

    return data.json();
  });
};
